import { useCallback, useEffect, useState } from "react";
import type { CustomerDto, RfqDto } from "@grizzly/shared";
import { rfqStatus } from "@grizzly/shared";
import { api, ApiClientError } from "../lib/apiClient.js";
import { formatDateTime } from "../lib/format.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { Modal } from "../components/ui/modal.js";
import { SelectInput, TextInput } from "../components/ui/field.js";
import { useToast } from "../lib/useToast.js";

export function RfqsPage() {
  const { push } = useToast();
  const { t } = useI18n();
  const [items, setItems] = useState<RfqDto[]>([]);
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.listRfqs(), api.listCustomers()]);
      setItems(r.items);
      setCustomers(c.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.createRfq({ customerId, description, source: "phone" });
      push(t("sales.inquiryRecorded"));
      setDescription("");
      setOpen(false);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customerId, description, load, push, t]);

  const columns: Column<RfqDto>[] = [
    {
      key: "number",
      header: t("sales.inquiry"),
      render: (r) => <span className="tabular font-medium text-ink">{r.number}</span>,
    },
    {
      key: "customer",
      header: t("common.fields.customer"),
      render: (r) => r.customerName,
    },
    { key: "source", header: t("sales.source"), render: (r) => r.source },
    {
      key: "status",
      header: t("common.status"),
      render: (r) => (
        <Badge tone={rfqStatus(r.status).tone}>
          {t(`status.rfq.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "when",
      header: t("sales.received"),
      render: (r) => formatDateTime(r.createdAt),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("sales.rfqsTitle")}
        description={t("sales.rfqsDescription")}
        actions={
          <Button variant="primary" onClick={() => setOpen(true)}>
            {t("sales.recordInquiry")}
          </Button>
        }
      />
      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}
      <DataTable
        caption={t("sales.rfqs")}
        loading={loading}
        rows={items}
        rowKey={(r) => r.id}
        columns={columns}
      />

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t("sales.recordInquiryTitle")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={submit} disabled={busy || !customerId}>
              {busy ? t("common.saving") : t("sales.saveInquiry")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <SelectInput
            label={t("common.fields.customer")}
            options={customers.map((c) => ({
              value: c.id,
              label: c.displayName || c.legalName,
            }))}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          />
          <TextInput
            label={t("sales.whatNeeded")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </Modal>
      <Card className="mt-4 border-dashed bg-surface-inset/40">
        <p className="text-sm text-ink-muted">{t("sales.convertHint")}</p>
      </Card>
    </>
  );
}
